import { FlexColumn, FlexRow, MONTH, StyledBanner, StyledButton } from '@gorila-shared-ui/components';
import { Checkbox } from 'baseui/checkbox';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'baseui/modal';
import { HeadingXSmall, ParagraphXSmall } from 'baseui/typography';
import { memo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRecoilValue } from 'recoil';
import { HISTORIC_REPORT_TIME_INTERVAL } from '../../../constants/app';
import { REPORT_INTERVAL_OPTIONS } from '../../../constants/reports';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { requestHistoricReportFile } from '../../../services/reportsService';
import { apiParamsState, isAllWorkspacesSelect } from '../../../storage/app';
import { Asset } from '../../../types/asset';
import { AssetPicker } from '../../shared/AssetPicker';
import { LabeledSelect } from '../../ui/LabeledSelect';
import { StyledDateTimeRangePicker } from '../../ui/StyledDateTimeRangePicker';

type Props = {
  onClose: () => void;
  isOpen: boolean;
  assetId?: string;
};

function HistoricReportModal({ onClose, isOpen, assetId }: Props) {
  const apiParams = useRecoilValue(apiParamsState);
  const [selectedAssetId, setSelectedAssetId] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { theme, classes } = useStyles();
  const [startDate, setStartDate] = useState<number | null>();
  const [endDate, setEndDate] = useState<number | null>();
  const [interval, setInterval] = useState(REPORT_INTERVAL_OPTIONS[0].id);
  const [datesError, setDatesError] = useState('');
  const { showPositiveFeedback } = useFeedback();
  const [isManualInterval, setManualInterval] = useState<boolean>(false);
  const [minStartDate, setMinStartDate] = useState<number>();
  const [maxEndDate, setMaxEndDate] = useState<number>();
  const isAllWorkspacesSelected = useRecoilValue(isAllWorkspacesSelect);

  useEffect(() => {
    if (!assetId || !isOpen) return;
    setSelectedAssetId(assetId);
    setManualInterval(false);
    const now = Date.now();
    setMinStartDate(now - HISTORIC_REPORT_TIME_INTERVAL * MONTH);
    setMaxEndDate(now);
  }, [assetId, isOpen]);

  useEffect(() => {
    if (isManualInterval) return;
    const now = Date.now();
    setEndDate(now);
    setStartDate(now - interval);
  }, [isManualInterval]);

  useEffect(() => {
    if (!interval) return;
    const now = Date.now();
    setEndDate(now);
    setStartDate(now - interval);
  }, [interval, isOpen]);

  const createHistoricReport = async () => {
    if (!selectedAssetId || !apiParams || !startDate || !endDate) return;
    setLoading(true);
    const { error } = await requestHistoricReportFile(
      selectedAssetId,
      startDate,
      endDate,
      apiParams.clientId,
      isAllWorkspacesSelected ? undefined : apiParams.subClientId,
    );
    if (!error) {
      resetModal();
      showPositiveFeedback('Tu reporte será procesado, visita la sección de descargas para obtenerlo.');
    } else if (error) {
      setError(`${error}`);
    }
    setLoading(false);
  };

  const resetModal = () => {
    onClose();
    setSelectedAssetId(undefined);
    setStartDate(undefined);
    setEndDate(undefined);
    setInterval(REPORT_INTERVAL_OPTIONS[0].id);
    setError('');
    setDatesError('');
  };

  const onAssetChange = (asset: Asset) => {
    setSelectedAssetId(asset._id);
  };

  return createPortal(
    <Modal
      animate
      closeable
      onClose={resetModal}
      isOpen={isOpen}
      size={600}
    >
      <ModalHeader>
        <FlexRow
          gap={theme.sizing.scale200}
          classNames={classes.centeredStart}
        >
          <HeadingXSmall margin={0}>Histórico de posiciones</HeadingXSmall>
        </FlexRow>
      </ModalHeader>
      <ModalBody>
        {error && (
          <StyledBanner
            title="*Error*"
            kind="negative"
            action={{
              label: 'Label',
              onClick: () => {
                setError('');
              },
            }}
          >
            {error}
          </StyledBanner>
        )}
        <FlexColumn>
          <AssetPicker
            assetId={selectedAssetId}
            onChange={onAssetChange}
            disabled={!!assetId}
          />
          <LabeledSelect
            label="Intervalo de tiempo"
            onChange={(params) => {
              setInterval((params.option?.id as number) || 0);
            }}
            value={[{ id: interval }]}
            options={REPORT_INTERVAL_OPTIONS}
            disabled={isManualInterval}
          />
          <Checkbox
            checked={isManualInterval}
            checkmarkType="toggle"
            onChange={(e) => setManualInterval(e.target.checked)}
            labelPlacement="right"
          >
            Intervalo Manual
          </Checkbox>
          {isManualInterval && (
            <>
              <StyledDateTimeRangePicker
                todayIsMaxDate={false}
                dates={[startDate ? new Date(startDate) : undefined, endDate ? new Date(endDate) : undefined]}
                onDatesChange={(dates) => {
                  if (Array.isArray(dates)) {
                    if (dates.length > 1 && dates[0] && dates[1]) {
                      setStartDate(dates[0].getTime());
                      setEndDate(dates[1].getTime());
                    } else if (dates[0]) {
                      setStartDate(dates[0].getTime());
                      setEndDate(null);
                    }
                  }
                }}
                onError={setDatesError}
                startLabel="Fecha y hora de inicio:"
                endLabel="Fecha y hora fin:"
                maxEndDate={maxEndDate}
                minStartDate={minStartDate}
              />
              {datesError && (
                <ParagraphXSmall
                  margin={0}
                  color={theme.colors.negative}
                >
                  {datesError}
                </ParagraphXSmall>
              )}
            </>
          )}
        </FlexColumn>
      </ModalBody>
      <ModalFooter>
        <FlexRow
          gap={theme.sizing.scale300}
          classNames={classes.centeredEnd}
        >
          <StyledButton
            kind="tertiary"
            onClick={resetModal}
          >
            Cancelar
          </StyledButton>
          <StyledButton
            onClick={createHistoricReport}
            disabled={!selectedAssetId}
            isLoading={loading}
          >
            Solicitar
          </StyledButton>
        </FlexRow>
      </ModalFooter>
    </Modal>,
    document.body,
  );
}

export default memo(HistoricReportModal);
